import type React from 'react';
/**
 * Нормы расхода материалов — раздел сметчика (миграция 0217).
 *
 * Норма расхода это коэффициент, на который умножается проектный объём
 * материала, чтобы получить объём к списанию: потери при резке трубы, бой
 * кирпича, нахлёст рулонного материала. Справочник существовал с самого начала,
 * но наружу не выходил — правило можно было завести только запросом в базу.
 * Отдел просил экран: «нормы для каждого договора конкретные» (письмо СЦО
 * 07.09.2026).
 *
 * Правил два вида: общий класс материала (действует везде) и правило договора
 * (сильнее общего). Оба вида показаны в одном списке — сметчик должен видеть
 * ровно то, что применится, а не два разных экрана.
 */
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useAgentPermission } from '@entities/user';
import {
  apiErrorText,
  useCreateMaterialNormMutation,
  useDeleteMaterialNormMutation,
  useListContractMaterialNormsQuery,
  useListGlobalMaterialNormsQuery,
  useResolveMaterialNormMutation,
  useUpdateMaterialNormMutation,
} from '@shared/api';
import type { ContractMaterialNorm } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Input } from '@shared/ui/Input';


const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: inherit;
  font: inherit;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
  }

  th.num {
    text-align: right;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  min-width: 150px;
`;

/** Коэффициент 1.033 в поле ввода — это 3,3 % потерь. */
function pctFromFactor(factor: number): string {
  return ((factor - 1) * 100).toFixed(2).replace(/\.?0+$/, '');
}

function factorFromPct(pct: string): number | null {
  const v = Number(pct.trim().replace(',', '.'));
  if (!Number.isFinite(v) || v < 0) return null;
  return 1 + v / 100;
}

function matchKindLabel(kind: string): string {
  switch (kind) {
    case 'substring':
      return 'вхождение';
    case 'regex':
      return 'регулярное выражение';
    case 'exact':
      return 'точное совпадение';
    case 'code':
      return 'по шифру';
    default:
      return kind;
  }
}

function supplyLabel(scope: string | null): string {
  switch (scope) {
    case 'customer':
      return 'поставка Заказчика';
    case 'contractor':
      return 'поставка Подрядчика';
    default:
      return 'любая поставка';
  }
}

const EMPTY_FORM = {
  material_class: '',
  match_kind: 'substring',
  match_pattern: '',
  unit: '',
  waste_pct: '',
  supply_scope: '',
  priority: '100',
  comment: '',
};

export function MaterialNorms() {
  const { can_write } = useAgentPermission('smeta');
  const [contractId, setContractId] = useState('');
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState('');
  const [probeResult, setProbeResult] = useState<string | null>(null);

  const trimmedContract = contractId.trim();
  const globalQ = useListGlobalMaterialNormsQuery(undefined, { skip: !!trimmedContract });
  const contractQ = useListContractMaterialNormsQuery(trimmedContract, { skip: !trimmedContract });
  const [createNorm, { isLoading: creating }] = useCreateMaterialNormMutation();
  const [updateNorm, { isLoading: updating }] = useUpdateMaterialNormMutation();
  const [deleteNorm] = useDeleteMaterialNormMutation();
  const [resolveNorm, { isLoading: resolving }] = useResolveMaterialNormMutation();

  const norms: ContractMaterialNorm[] = useMemo(
    () => (trimmedContract ? (contractQ.data?.norms ?? []) : (globalQ.data?.norms ?? [])),
    [trimmedContract, contractQ.data, globalQ.data],
  );

  function startEdit(n: ContractMaterialNorm) {
    setEditingId(n.id);
    setError(null);
    setForm({
      material_class: n.material_class,
      match_kind: n.match_kind,
      match_pattern: n.match_pattern,
      unit: n.unit ?? '',
      waste_pct: pctFromFactor(n.waste_factor),
      supply_scope: n.supply_scope ?? '',
      priority: String(n.priority),
      comment: n.comment ?? '',
    });
  }

  async function submit() {
    setError(null);
    const waste = factorFromPct(form.waste_pct);
    if (!form.material_class.trim() || !form.match_pattern.trim()) {
      setError('Заполните класс материала и что искать в наименовании.');
      return;
    }
    if (waste === null) {
      setError('Процент потерь должен быть числом от нуля.');
      return;
    }
    const body = {
      material_class: form.material_class.trim(),
      match_kind: form.match_kind,
      match_pattern: form.match_pattern.trim(),
      unit: form.unit.trim() || null,
      waste_factor: waste,
      supply_scope: form.supply_scope || null,
      priority: Number(form.priority) || 100,
      source: 'manual',
      comment: form.comment.trim() || null,
    };
    try {
      if (editingId) {
        await updateNorm({ normId: editingId, body }).unwrap();
      } else {
        await createNorm({ contractId: trimmedContract || null, body }).unwrap();
      }
      setForm({ ...EMPTY_FORM });
      setEditingId(null);
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось сохранить правило'));
    }
  }

  async function runProbe() {
    setProbeResult(null);
    if (!trimmedContract || !probe.trim()) return;
    try {
      const r = await resolveNorm({
        contractId: trimmedContract,
        body: { name: probe.trim() },
      }).unwrap();
      setProbeResult(
        r.matched && r.norm
          ? `${r.norm.material_class}: ${pctFromFactor(r.norm.waste_factor)} % (правил проверено: ${r.rules_considered})`
          : `Ни одно правило не подошло (правил проверено: ${r.rules_considered})`,
      );
    } catch {
      setProbeResult('Проверка недоступна');
    }
  }

  return (
    <Stack>
      <Card>
        <Row>
          <Field>
            Договор (id)
            <Input
              value={contractId}
              placeholder="пусто — общие классы для всех договоров"
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setContractId(e.target.value)}
            />
          </Field>
          <Muted>
            {trimmedContract
              ? 'Показаны правила договора и общие классы: именно они и применятся.'
              : 'Показаны только общие классы. Укажите договор, чтобы завести правило для него.'}
          </Muted>
        </Row>
      </Card>

      {can_write && (
        <Card>
          <Row>
            <Field>
              Класс материала
              <Input
                value={form.material_class}
                placeholder="арматура"
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, material_class: e.target.value })}
              />
            </Field>
            <Field>
              Как искать
              <Select
                value={form.match_kind}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, match_kind: e.target.value })}
              >
                <option value="substring">вхождение в наименование</option>
                <option value="regex">регулярное выражение</option>
                <option value="exact">точное совпадение</option>
                <option value="code">по шифру</option>
              </Select>
            </Field>
            <Field>
              Что искать
              <Input
                value={form.match_pattern}
                placeholder="арматур|а500"
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, match_pattern: e.target.value })}
              />
            </Field>
            <Field>
              Потери, %
              <Input
                value={form.waste_pct}
                placeholder="3,3"
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, waste_pct: e.target.value })}
              />
            </Field>
            <Field>
              Зона поставки
              <Select
                value={form.supply_scope}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, supply_scope: e.target.value })}
              >
                <option value="">любая</option>
                <option value="customer">Заказчика</option>
                <option value="contractor">Подрядчика</option>
              </Select>
            </Field>
            <Field>
              Приоритет
              <Input
                value={form.priority}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, priority: e.target.value })}
              />
            </Field>
            <Field>
              Комментарий
              <Input
                value={form.comment}
                placeholder="приложение №18 к договору"
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, comment: e.target.value })}
              />
            </Field>
            <Button type="button" disabled={creating || updating} onClick={() => void submit()}>
              {editingId ? 'Сохранить' : 'Добавить правило'}
            </Button>
            {editingId && (
              <Button
                $variant="ghost"
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm({ ...EMPTY_FORM });
                }}
              >
                Отмена
              </Button>
            )}
          </Row>
          {error && <Err>{error}</Err>}
          <Muted>
            Процент вводится как в приложении к договору: 3 % потерь — это коэффициент 1,03.
            Правило договора сильнее общего класса, при равной области — правило с большим
            приоритетом.
          </Muted>
        </Card>
      )}

      {trimmedContract && (
        <Card>
          <Row>
            <Field>
              Проверить наименование
              <Input
                value={probe}
                placeholder="Арматура 16 А-III А500С ГОСТ 34028-2016"
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setProbe(e.target.value)}
              />
            </Field>
            <Button
              $variant="secondary"
              type="button"
              disabled={resolving}
              onClick={() => void runProbe()}
            >
              Какая норма подставится
            </Button>
            {probeResult && <Muted>{probeResult}</Muted>}
          </Row>
        </Card>
      )}

      <Card>
        <Table>
          <thead>
            <tr>
              <th>Класс материала</th>
              <th>Как искать</th>
              <th>Что искать</th>
              <th className="num">Потери</th>
              <th>Зона поставки</th>
              <th>Область</th>
              <th className="num">Приоритет</th>
              <th>Комментарий</th>
              {can_write && <th />}
            </tr>
          </thead>
          <tbody>
            {norms.map((n) => (
              <tr key={n.id}>
                <td>{n.material_class}</td>
                <td>{matchKindLabel(n.match_kind)}</td>
                <td>
                  <code>{n.match_pattern}</code>
                </td>
                <td className="num">{pctFromFactor(n.waste_factor)} %</td>
                <td>{supplyLabel(n.supply_scope)}</td>
                <td>
                  <Badge $tone={n.contract_id ? 'info' : 'neutral'}>
                    {n.contract_id ? 'договор' : 'общий класс'}
                  </Badge>
                </td>
                <td className="num">{n.priority}</td>
                <td>{n.comment ?? '—'}</td>
                {can_write && (
                  <td>
                    <Button $variant="ghost" $size="sm" type="button" onClick={() => startEdit(n)}>
                      Изменить
                    </Button>
                    <Button
                      $variant="ghost"
                      $size="sm"
                      type="button"
                      onClick={() => void deleteNorm(n.id)}
                    >
                      Удалить
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
        {norms.length === 0 && <Muted>Правил пока нет.</Muted>}
      </Card>
    </Stack>
  );
}
